import { db } from '../firebase/config';
import { 
    doc, 
    updateDoc, 
    arrayUnion, 
    getDoc,
} from 'firebase/firestore';
import { cleanNumeric } from './segurosUtils';

/**
 * Listado estático centralizado de marcas de tarjeta admitidas por la plataforma.
 * Reutilizado dinámicamente en los dropdowns de carga de asegurados y endosos.
 */
export const MARCAS_TARJETA_ADMITIDAS = ['VISA', 'MASTERCARD', 'AMEX', 'NARANJA', 'CABAL'];

/**
 * Formatea un medio de pago (CBU o Tarjeta) a su representación visual abreviada y segura.
 * Evita exponer información sensible de tarjetas y CBUs en las tablas del sistema.
 * * @param {object} medio - Objeto que contiene los datos del medio de pago.
 * @param {string} tipoFormaPago - Tipo de pago ('CBU', 'Tarjeta de Credito', etc.)
 * @returns {string} String formateado (ej. "VISA - GALICIA - XXXX 1234" o "CBU - SANTANDER - **** 123456")
 */
export const formatMedioPagoAbreviado = (medio, tipoFormaPago) => {
    if (!medio) return 'S/D';
    if (typeof medio === 'string') {
        if (medio.includes('-') || medio.includes('****') || medio.includes('XXXX')) {
            return medio.toUpperCase(); // Ya viene formateado legacy
        }
        return medio;
    }

    const tipoUpper = String(tipoFormaPago || '').toUpperCase().trim();

    if (tipoUpper.includes('TARJETA') || tipoUpper.includes('CREDITO')) {
        const marca = (medio.marca || 'TARJETA').toUpperCase().trim();
        const banco = (medio.banco || 'S/D').toUpperCase().trim();
        const num = medio.numeroTarjeta || medio.numero || '';
        const last4 = String(num).slice(-4) || 'XXXX';
        return `${marca} - ${banco} - XXXX ${last4}`.toUpperCase();
    }

    if (tipoUpper.includes('CBU')) {
        const banco = (medio.banco || 'S/D').toUpperCase().trim();
        const numCbu = medio.numeroCbu || medio.cbu || medio.numero || '';
        const last6 = String(numCbu).slice(-6) || '000000';
        return `CBU - ${banco} - **** ${last6}`.toUpperCase();
    }

    return String(tipoFormaPago || 'OTRO').toUpperCase();
};

/**
 * Valida de manera estricta la integridad de los datos de un medio de pago antes de guardarlo.
 * * @param {object} medioData - Datos del medio de pago a validar.
 * @param {string} tipoFormaPago - Tipo de pago ('CBU' o 'Tarjeta de Credito').
 * @throws {Error} Si algún campo obligatorio está vacío o tiene formato inválido.
 */
export const validarMedioPago = (medioData, tipoFormaPago) => {
    const tipoUpper = String(tipoFormaPago || '').toUpperCase().trim();

    if (tipoUpper.includes('CBU')) {
        const cbuLimpio = cleanNumeric(medioData.cbu || medioData.numeroCbu);
        if (!medioData.banco?.trim()) {
            throw new Error("Debe ingresar el nombre del Banco para registrar el CBU.");
        }
        if (cbuLimpio.length !== 22) {
            throw new Error("El número de CBU debe contener exactamente 22 dígitos numéricos.");
        }
    } else if (tipoUpper.includes('TARJETA') || tipoUpper.includes('CREDITO')) {
        const numTarjetaLimpio = cleanNumeric(medioData.numero || medioData.numeroTarjeta);
        if (!medioData.marca?.trim()) {
            throw new Error("Debe seleccionar la marca de la Tarjeta (VISA, MASTERCARD, AMEX, NARANJA).");
        }
        if (!medioData.banco?.trim()) {
            throw new Error("Debe ingresar el Banco Emisor de la Tarjeta.");
        }
        if (numTarjetaLimpio.length < 15 || numTarjetaLimpio.length > 16) {
            throw new Error("El número de Tarjeta debe contener entre 15 y 16 dígitos.");
        }
        if (!medioData.vencimientoTarjeta?.trim() && !medioData.vto?.trim()) {
            throw new Error("Debe ingresar la fecha de vencimiento (MM/AA) de la tarjeta.");
        }
    } else {
        throw new Error(`La forma de pago '${tipoFormaPago}' no requiere almacenamiento de coordenadas bancarias.`);
    }
};

/**
 * Registra de forma transaccional un nuevo CBU o Tarjeta en la ficha del Asegurado en Firestore.
 * Sincroniza las nomenclaturas de campos para evitar discrepancias de propiedades.
 * * @param {string} clienteId - ID único del asegurado en la colección de clientes.
 * @param {object} medioData - Datos crudos provenientes del formulario de carga.
 * @param {string} tipoFormaPago - Tipo de pago seleccionado ('CBU' o 'Tarjeta de Credito').
 * @returns {Promise<string>} Retorna la etiqueta visual abreviada del medio de pago registrado con éxito.
 */
export const registrarMedioPagoCliente = async (clienteId, medioData, tipoFormaPago) => {
    if (!clienteId) throw new Error("ID de cliente requerido para registrar medio de pago.");
    
    // 1. Validamos la consistencia de los datos
    validarMedioPago(medioData, tipoFormaPago);

    const clienteRef = doc(db, 'public/data/clientes', clienteId);
    const tipoUpper = String(tipoFormaPago || '').toUpperCase().trim();
    const timestampStatic = new Date().toISOString();
    
    let subcoleccionDestino = '';
    let payload = {};

    if (tipoUpper.includes('CBU')) {
        subcoleccionDestino = 'cbus';
        payload = {
            banco: medioData.banco.trim().toUpperCase(),
            numeroCbu: cleanNumeric(medioData.cbu || medioData.numeroCbu),
            fechaAlta: timestampStatic
        };
    } else {
        subcoleccionDestino = 'tarjetas';
        payload = {
            marca: medioData.marca.trim().toUpperCase(),
            banco: medioData.banco.trim().toUpperCase(),
            numeroTarjeta: cleanNumeric(medioData.numero || medioData.numeroTarjeta),
            vencimientoTarjeta: (medioData.vencimientoTarjeta || medioData.vto || '').trim(),
            fechaAlta: timestampStatic
        };
    }

    // 2. Persistencia atómica usando arrayUnion en el documento del Asegurado
    await updateDoc(clienteRef, {
        [subcoleccionDestino]: arrayUnion(payload)
    });

    // 3. Devolvemos la nomenclatura abreviada de control
    return formatMedioPagoAbreviado(payload, tipoFormaPago);
};

/**
 * Obtiene y mapea los medios de pago disponibles de un asegurado, formateados listos para dropdowns.
 * * @param {string} clienteId - ID único del asegurado.
 * @param {string} tipoFormaPago - Filtra opcionalmente por tipo ('CBU' o 'Tarjeta de Credito')
 * @returns {Promise<Array<{id: string, label: string, original: object}>>} Lista de medios unificada
 */
export const obtenerMediosPagoCliente = async (clienteId, tipoFormaPago) => {
    if (!clienteId) return [];
    
    try {
        const snap = await getDoc(doc(db, 'public/data/clientes', clienteId));
        if (!snap.exists()) return [];

        const data = snap.data();
        const tipoUpper = String(tipoFormaPago || '').toUpperCase().trim();
        const results = [];

        if (tipoUpper.includes('TARJETA') || tipoUpper.includes('CREDITO') || !tipoFormaPago) {
            const tarjetas = data.tarjetas || [];
            tarjetas.forEach(t => {
                results.push({
                    id: String(t.numeroTarjeta || t.numero || ''),
                    label: formatMedioPagoAbreviado(t, 'Tarjeta de Crédito'),
                    original: t
                });
            });
        }

        if (tipoUpper.includes('CBU') || !tipoFormaPago) {
            const cbus = data.cbus || [];
            cbus.forEach(c => {
                results.push({
                    id: String(c.numeroCbu || c.cbu || c.numero || ''),
                    label: formatMedioPagoAbreviado(c, 'CBU'),
                    original: c
                });
            });
        }

        return results;
    } catch (error) {
        console.error("Error al recuperar medios de pago del cliente:", error);
        return [];
    }
};